import { ColumnDefinition } from 'components/TableColumn/TableColumn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { camelCase } from 'lodash';
import omit from 'lodash/omit';
import {
  OutdoorDeviceMetricType,
  OutdoorDeviceReportTableRecord,
  ReportGran
} from 'core/report/ReportData';
import { DefaultReportManager } from 'core/report/ReportManager';
import { useCoreContext } from 'contexts/coreContext';
import { MOMENT_TIMEZONE } from 'core';
import moment from 'moment-timezone';
import { useOutdoorDeviceReportDataProvider } from './dataProviderFactory';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  WeatherVerificationCampaignOption,
  WeatherVerificationStoreOption,
  toStoreKey
} from 'core/report/WeatherVerification';
import { downloadCsv } from 'utils/DownloadUtils';
import {
  MAX_QUERY_DAYS as MAX_FILTER_OPTION_DAYS,
  QUERY_WINDOW_MONTHS as FILTER_OPTION_WINDOW_MONTHS,
  exceedsMaxQueryDays,
  getTaipeiToday
} from './WeatherVerification/WeatherVerificationTabModel';

export const KEY_COLUMN = 'deliveryTargetName';

/** 「+ 篩選條件」可以加的維度，順序即下拉選單的順序。 */
export enum OutdoorDeviceFilterDimension {
  CAMPAIGN = 'campaign',
  STORE = 'store'
}

const OPTION_DATE_FORMAT = 'YYYY-MM-DD';

const MAX_QUERY_DAYS = 7;

/**
 * 活動 / 門市選項借用機台投遞驗證的選項 API，它只接受兩種區間，超出會回 400：
 * - 最多 3 天，對應後端 OutdoorDeliveryVerificationService.MAX_QUERY_DAYS
 * - 起始日不早於今天往前 3 個月，對應後端 OutdoorDeliveryVerificationService.MAX_LOOKBACK_MONTHS
 * 數字與日期判斷沿用驗證 Tab 的 MAX_QUERY_DAYS / QUERY_WINDOW_MONTHS，後端調整時兩個 Tab 一起改。
 * 參數是只含日期的 YYYY-MM-DD，與實際送給選項 API 的值相同。
 */
const isFilterOptionRangeExceeded = (from: string, to: string) =>
  exceedsMaxQueryDays(from, to) ||
  moment(from, OPTION_DATE_FORMAT).isBefore(
    moment(getTaipeiToday()).subtract(FILTER_OPTION_WINDOW_MONTHS, 'months')
  );

export const hourLabelValueMap = {
  '0': '12:00.00 AM',
  '1': '1:00.00 AM',
  '2': '2:00.00 AM',
  '3': '3:00.00 AM',
  '4': '4:00.00 AM',
  '5': '5:00.00 AM',
  '6': '6:00.00 AM',
  '7': '7:00.00 AM',
  '8': '8:00.00 AM',
  '9': '9:00.00 AM',
  '10': '10:00.00 AM',
  '11': '11:00.00 AM',
  '12': '12:00.00 PM',
  '13': '1:00.00 PM',
  '14': '2:00.00 PM',
  '15': '3:00.00 PM',
  '16': '4:00.00 PM',
  '17': '5:00.00 PM',
  '18': '6:00.00 PM',
  '19': '7:00.00 PM',
  '20': '8:00.00 PM',
  '21': '9:00.00 PM',
  '22': '10:00.00 PM',
  '23': '11:00.00 PM'
};

/**
 * CSV 的小時欄標頭。畫面沿用上面的 `12:00.00 AM`，CSV 給客戶看，改用一般的 `12:00 AM`。
 */
export const csvHourLabelValueMap: Record<string, string> = Object.fromEntries(
  Array.from({ length: 24 }, (_, hour) => [
    hour.toString(),
    `${hour % 12 === 0 ? 12 : hour % 12}:00 ${hour < 12 ? 'AM' : 'PM'}`
  ])
);

type OutdoorDeviceReportQueryOptions = {
  from: string;
  to: string;
  metricType: OutdoorDeviceMetricType;
  gran: ReportGran;
  campaignId?: number;
  /** 見 toStoreKey */
  storeKeys: string[];
};

const reportManager = new DefaultReportManager();

export const useOutdoorDeviceReportModel = (
  l1ObjectId: number,
  startDate: string,
  endDate: string,
  deliveryUnit?: DeliveryUnit,
  /**
   * 這個廣告群組所屬的銷售渠道是不是電子紙。
   *
   * 電子紙機台沒有人流統計，所以「資料類型」只留曝光數，預設也直接落在曝光數；
   * 其他戶外渠道維持原本人流數 / 曝光數兩個選項、預設人流數。
   */
  isEpaper = false,
  /**
   * 是否正停在機台投遞成效這個 Tab。篩選條件的活動 / 門市選項只在這個 Tab 開著時載入。
   */
  active = true
) => {
  const { loading, callAPIs } = useCallAPI();
  // 選項另用一組，載入時不會觸發整頁的 loading，也不會讓圖表閃成空狀態
  const { callAPIs: callOptionAPIs } = useCallAPI();
  const core = useCoreContext();
  const localeMeta = core?.accountManager.localeMeta;
  const timezone = localeMeta?.timezone || '+08:00';

  const [metricType, setMetricType] = useState<OutdoorDeviceMetricType>(
    isEpaper
      ? OutdoorDeviceMetricType.IMPRES
      : OutdoorDeviceMetricType.E_PUBLISHER_IMPRES
  );
  const metricTypeOptions = useMemo(
    () =>
      createSelectOptionsFromEnum(
        OutdoorDeviceMetricType,
        'outdoorDeviceMetric.labels.',
        isEpaper ? [OutdoorDeviceMetricType.E_PUBLISHER_IMPRES] : undefined,
        camelCase
      ),
    [isEpaper]
  );
  const [gran, setGran] = useState<ReportGran>(ReportGran.DAY);
  const granOptions = useMemo(
    () => [
      {
        value: ReportGran.DAY,
        label: i18n.t('outdoorDeviceGran.labels.day')
      },
      {
        value: ReportGran.HOUR,
        label: i18n.t('outdoorDeviceGran.labels.hour')
      }
    ],
    []
  );
  const [from, setFrom] = useState<string>(
    moment(startDate).startOf(gran).format('YYYY-MM-DD HH:mm:ss')
  );
  const [to, setTo] = useState<string>(
    moment(endDate).endOf(gran).format('YYYY-MM-DD HH:mm:ss')
  );

  const dayRangeError = useMemo(() => {
    if (gran !== ReportGran.HOUR) {
      return undefined;
    }
    const fromMoment = moment(from);
    const toMoment = moment(to);
    const diffDays = toMoment.diff(fromMoment, 'days') + 1;
    if (diffDays > MAX_QUERY_DAYS) {
      return i18n.t('outdoorDeviceReport.errors.maxQueryHours', {
        days: MAX_QUERY_DAYS,
        hours: MAX_QUERY_DAYS * 24
      });
    }
    return undefined;
  }, [from, to, gran]);

  const [reportTableData, setReportTableData] = useState<
    OutdoorDeviceReportTableRecord[]
  >([]);
  const [tableSummaryRecord, setTableSummaryRecord] =
    useState<OutdoorDeviceReportTableRecord>();
  // query options 是最後拿來 call api 的參數
  // 其他的 state 只是拿來做 UI 呈現，改變不會觸發資料重新載入
  const [queryOptions, setQueryOptions] =
    useState<OutdoorDeviceReportQueryOptions>({
      from: startDate,
      to: endDate,
      metricType,
      gran,
      campaignId: undefined,
      storeKeys: []
    });

  const [filterDimensions, setFilterDimensions] = useState<
    OutdoorDeviceFilterDimension[]
  >([]);
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
  const [storeKeys, setStoreKeys] = useState<string[]>([]);
  const [campaignOptionRecords, setCampaignOptionRecords] = useState<
    WeatherVerificationCampaignOption[]
  >([]);
  const [storeOptionRecords, setStoreOptionRecords] = useState<
    WeatherVerificationStoreOption[]
  >([]);

  const filterDimensionsCanAdd = useMemo(
    () =>
      Object.values(OutdoorDeviceFilterDimension).filter(
        (dimension) => !filterDimensions.includes(dimension)
      ),
    [filterDimensions]
  );

  const campaignOptions: SelectOptions[] = useMemo(
    () =>
      campaignOptionRecords.map((record) => ({
        label: record.campaignName,
        value: record.campaignId
      })),
    [campaignOptionRecords]
  );

  const storeOptions: SelectOptions[] = useMemo(
    () =>
      storeOptionRecords.map((record) => ({
        label: record.storeName,
        value: toStoreKey(record)
      })),
    [storeOptionRecords]
  );

  // 選項只看日期，同一天內改時間不必重載
  const optionFrom = moment(from).format(OPTION_DATE_FORMAT);
  const optionTo = moment(to).format(OPTION_DATE_FORMAT);
  const filterRangeExceeded = isFilterOptionRangeExceeded(optionFrom, optionTo);

  // 區間超出選項 API 的限制時不載選項，篩選列也不能操作。已選的活動 / 門市一併清掉：
  // 選項清空後畫面上看不出選了什麼、也改不了，留著就會在查詢時送出看不到的條件。
  // 改回限制內的區間時，下面兩個 effect 會因日期改變而重新載入選項。
  useEffect(() => {
    if (!filterRangeExceeded) {
      return;
    }
    setCampaignOptionRecords([]);
    setStoreOptionRecords([]);
    setCampaignId(undefined);
    setStoreKeys([]);
  }, [filterRangeExceeded]);

  // 活動選項跟著區間走。已選的活動不在新清單裡就清掉，避免送出畫面上看不到的條件。
  // 載入失敗時把選項清空，不擋查詢。
  useEffect(() => {
    if (!active || filterRangeExceeded) {
      return;
    }
    let outdated = false;
    const applyCampaignOptions = (
      records: WeatherVerificationCampaignOption[]
    ) => {
      if (outdated) {
        return;
      }
      setCampaignOptionRecords(records);
      setCampaignId((prev) =>
        prev !== undefined &&
        records.some((record) => record.campaignId === prev)
          ? prev
          : undefined
      );
    };
    callOptionAPIs(
      [
        () =>
          reportManager.getWeatherVerificationCampaigns(
            l1ObjectId,
            optionFrom,
            optionTo
          )
      ],
      (records?: WeatherVerificationCampaignOption[]) =>
        applyCampaignOptions(records ?? []),
      () => applyCampaignOptions([])
    );
    return () => {
      outdated = true;
    };
  }, [
    active,
    filterRangeExceeded,
    l1ObjectId,
    optionFrom,
    optionTo,
    callOptionAPIs
  ]);

  // 門市選項跟著區間與活動走，未選活動時是整個廣告群組的門市。
  // 換活動後已選門市可能不在新清單裡，濾掉避免送出無效的 storeKeys。
  useEffect(() => {
    if (!active || filterRangeExceeded) {
      return;
    }
    let outdated = false;
    const applyStoreOptions = (records: WeatherVerificationStoreOption[]) => {
      if (outdated) {
        return;
      }
      setStoreOptionRecords(records);
      setStoreKeys((prev) =>
        prev.filter((storeKey) =>
          records.some((record) => toStoreKey(record) === storeKey)
        )
      );
    };
    callOptionAPIs(
      [
        () =>
          reportManager.getWeatherVerificationStores(
            l1ObjectId,
            campaignId,
            optionFrom,
            optionTo
          )
      ],
      (records?: WeatherVerificationStoreOption[]) =>
        applyStoreOptions(records ?? []),
      () => applyStoreOptions([])
    );
    return () => {
      outdated = true;
    };
  }, [
    active,
    filterRangeExceeded,
    l1ObjectId,
    campaignId,
    optionFrom,
    optionTo,
    callOptionAPIs
  ]);

  const onAddFilter = useCallback((dimension: OutdoorDeviceFilterDimension) => {
    setFilterDimensions((prev) =>
      prev.includes(dimension) ? prev : [...prev, dimension]
    );
  }, []);

  // 移除條件行時一併清掉該維度的值，否則它會在看不到的情況下繼續過濾
  const onRemoveFilter = useCallback(
    (dimension: OutdoorDeviceFilterDimension) => {
      setFilterDimensions((prev) => prev.filter((item) => item !== dimension));
      if (dimension === OutdoorDeviceFilterDimension.CAMPAIGN) {
        setCampaignId(undefined);
      } else {
        setStoreKeys([]);
      }
    },
    []
  );

  const onCampaignChange = useCallback((value?: number | string | null) => {
    setCampaignId(value ? +value : undefined);
  }, []);

  const onStoreChange = useCallback((values?: string[] | null) => {
    setStoreKeys(values ?? []);
  }, []);

  const onMetricTypeChange = useCallback((value: string | number) => {
    setMetricType(value as OutdoorDeviceMetricType);
  }, []);

  const onGranChange = useCallback((value: string | number) => {
    setGran(value as ReportGran);
  }, []);

  const onDayRangeChange = useCallback(
    (from = '', to = '') => {
      setFrom(moment(from).startOf(gran).format('YYYY-MM-DD HH:mm:ss'));
      setTo(moment(to).endOf(gran).format('YYYY-MM-DD HH:mm:ss'));
    },
    [gran]
  );

  const dataProvider = useOutdoorDeviceReportDataProvider(
    queryOptions.gran,
    deliveryUnit
  );

  const chartData = useMemo(() => {
    return dataProvider.getChartData(
      tableSummaryRecord,
      queryOptions.metricType
    );
  }, [dataProvider, tableSummaryRecord, queryOptions.metricType]);

  const columns: ColumnDefinition[] = useMemo(() => {
    // DayDataProvider 只吃一個參數，HourDataProvider 吃三個
    return dataProvider.getColumns(
      tableSummaryRecord,
      queryOptions.from,
      queryOptions.to
    );
  }, [dataProvider, tableSummaryRecord, queryOptions.from, queryOptions.to]);

  const metricTotalSum = useMemo(() => {
    if (!tableSummaryRecord) {
      return 0;
    }
    return Object.keys(tableSummaryRecord).reduce((sum, key) => {
      if (key !== KEY_COLUMN) {
        return sum + +tableSummaryRecord[key];
      }
      return sum;
    }, 0);
  }, [tableSummaryRecord]);

  const fetchReportData = useCallback(() => {
    const getDateWithTimezone = (time: string) => {
      const momentTimezone = MOMENT_TIMEZONE[timezone]
        ? MOMENT_TIMEZONE[timezone]
        : MOMENT_TIMEZONE['+08:00'];
      return moment.tz(time, momentTimezone).format();
    };
    const { from, to, metricType, gran, campaignId, storeKeys } = queryOptions;
    callAPIs(
      [
        () =>
          reportManager.getOutdoorDeviceReportData(
            l1ObjectId,
            getDateWithTimezone(from),
            getDateWithTimezone(to),
            metricType,
            gran,
            campaignId,
            storeKeys
          )
      ],
      (data) => {
        const normalizedRecords = data.records.map((record) => ({
          [KEY_COLUMN]: record.deliveryTargetName,
          ...dataProvider.normalizeData(
            omit(record, KEY_COLUMN),
            queryOptions.from,
            queryOptions.to
          )
        }));
        setReportTableData(normalizedRecords);

        const summaryRecord: OutdoorDeviceReportTableRecord =
          dataProvider.getSummaryRecord(
            data.records,
            queryOptions.from,
            queryOptions.to
          );
        setTableSummaryRecord(summaryRecord);
      }
    );
  }, [l1ObjectId, queryOptions, timezone, callAPIs, dataProvider]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const onQueryBtnClick = useCallback(() => {
    setQueryOptions({
      from,
      to,
      metricType,
      gran,
      campaignId,
      storeKeys
    });
    // 清空 table data
    // 避免因為舊資料拿不到對應的 metric 資料造成圖表呈現異常
    setTableSummaryRecord(undefined);
    setReportTableData([]);
  }, [from, to, metricType, gran, campaignId, storeKeys]);

  const canExportCsv = !loading && reportTableData.length > 0;

  // 只用畫面上已經拿到的資料，不另外打 API；內容是已套用查詢條件的表格資料列，不含總計列
  const onExportCsv = useCallback(() => {
    if (reportTableData.length === 0) {
      return;
    }
    // 與表格預設排序一致：名稱由大到小，比較方式同表格的預設比較
    const sortedRecords = [...reportTableData].sort((a, b) =>
      a[KEY_COLUMN] < b[KEY_COLUMN] ? 1 : -1
    );
    const { headers, rows } = dataProvider.getCsvContent(
      sortedRecords,
      queryOptions.from,
      queryOptions.to
    );
    const fileName = i18n.t('outdoorDeviceReport.labels.csvFileName', {
      from: moment(queryOptions.from).format('YYYYMMDD'),
      to: moment(queryOptions.to).format('YYYYMMDD'),
      metric: i18n.t(`outdoorDeviceMetric.labels.${queryOptions.metricType}`)
    });
    downloadCsv(fileName, headers, rows);
  }, [
    dataProvider,
    reportTableData,
    queryOptions.from,
    queryOptions.to,
    queryOptions.metricType
  ]);

  return {
    loading,
    metricType,
    metricTypeOptions,
    gran,
    granOptions,
    from,
    to,
    columns,
    reportTableData,
    metricTotalSum,
    tableSummaryRecord,
    queryOptions,
    chartData,
    dayRangeError,
    filterRangeExceeded,
    filterRangeHint: filterRangeExceeded
      ? i18n.t('outdoorDeviceReport.hints.filterRangeLimit', {
          days: MAX_FILTER_OPTION_DAYS,
          months: FILTER_OPTION_WINDOW_MONTHS
        })
      : undefined,
    filterDimensions,
    filterDimensionsCanAdd,
    campaignId,
    storeKeys,
    campaignOptions,
    storeOptions,
    canExportCsv,
    onMetricTypeChange,
    onGranChange,
    onDayRangeChange,
    onAddFilter,
    onRemoveFilter,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick,
    onExportCsv
  };
};
